<template>
  <div>
    <v-container fill-height fluid>
    <v-row>
      <v-spacer></v-spacer>
      <v-btn
        v-if="can('create teachers')"
        @click="showSchoolTeacherModal(0, 0, 0)"
        color="primary"
        dark
        class="mb-2"
      >{{ $t("NEW") }}
      </v-btn>
    </v-row>
    </v-container>
    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="totalItems"
      :no-data-text="$t('NO_DATA')"
      :no-results-text="$t('NO_DATA')"
      hide-default-footer
      :options.sync="pagination"
      :loading="loading"
      class="elevation-1"
    >

      <template v-slot:item.updated_at="{ item }">
            {{ item.updated_at | timeFormat }}
      </template>
      <template v-slot:item.classes="{ item }">
           <span v-if="item.options && item.options.classes">{{ item.options.classes }}</span>
      </template>
      <template v-slot:item.actions="{ item }">
            <v-layout wrap>
              <v-flex class="text-no-wrap">
                <v-btn
                  v-if="can('delete schools') || can('create teachers') || can('edit teachers')"
                  icon
                  color="red"
                  @click="deleteSchoolTeacher(item.id)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
                <v-btn
                  v-if="can('edit schools') || can('create teachers') || can('edit teachers')"
                  icon
                  color="blue"
                  @click="showSchoolTeacherModal(item.id, item.school_id, item.school_locations_id)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
      </template>
    </v-data-table>
    <Paginator
      :total-items="totalItems"
      :page.sync="pagination.page"
      :items-per-page.sync="pagination.itemsPerPage"
      @change="fetchSchoolTeachers"
    />
  </div>
</template>

<script>
  import { mapActions } from 'vuex';

  import { teachersSchoolsService } from '../../../utils/services/teachersSchools.service';
  import SchoolTeacherModal from '../../modals/Teachers/SchoolTeacherModal';
  import Paginator from '../../shared/Paginator';

  export default {
    name: 'TeacherSchoolDetails',
    components: { Paginator },
    props: {
      teacher: {
        type: Object,
        default: () => {},
      },
    },
    data() {
      return {
        loading: false,
        items: [],
        totalItems: 0,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortDesc: [false],
          sortBy: ['school_name'],
        },
        headers: [
          {
            text: this.$t('fields.school'),
            align: 'left',
            value: 'school_name',
          },
          { text: this.$t('fields.code'), value: 'code' },
          { text: this.$t('fields.province'), value: 'province' },
          { text: this.$t('fields.city'), value: 'city' },
          { text: this.$t('fields.address'), value: 'street' },
          { text: this.$t('fields.email'), value: 'email' },
          { text: this.$t('fields.phone'), value: 'phone' },
          { text: this.$t('fields.subject'), value: 'subject_name' },
          { text: this.$t('fields.classes'), value: 'classes' },
          { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'center' },
        ],
      };
    },
    computed: {
      requestFilters() {
        const filters = {};
        if (this.pagination.sortBy.length === 1 && this.pagination.sortDesc.length === 1) {
          const { sortBy, sortDesc } = this.pagination;
          filters.sortBy = sortBy;
          filters.sortDesc = sortDesc;
        }
        return filters;
      },
    },
    methods: {
      ...mapActions('confirm', ['showConfirm']),
      ...mapActions('modal', ['showModal', 'hideModal']),
      ...mapActions('alert', ['showToast']),

      async showSchoolTeacherModal(id, schoolId, schoolLocationId) {
        this.showModal({
          component: SchoolTeacherModal,
            data: {
              stored: {
                id,
                school_id: schoolId,
                school_location_id: schoolLocationId,
                teacher_id: this.teacher.id,
              },
          },
          options: {
            title: this.$t('SCHOOL'),
            onSave: (
              form,
              error,
            ) => teachersSchoolsService.save(form)
            .then(() => {
              this.hideModal({});
              this.showToast({ type: 'success', message: this.$t('SAVED') });
              const { returnUrl } = this.$route.query;
              if (returnUrl) {
                this.$router.push({ path: returnUrl });
                return;
              }
              this.fetchSchoolTeachers(this.pagination.page);
            })
            .catch(error => {
              this.handleError(error, this.errors);
              this.fetchSchoolTeachers(this.pagination.page);
            }),
          },
        });
      },

      async fetchSchoolTeachers(forcePage) {
        this.loading = true;
        const { page, itemsPerPage } = this.pagination;
        return teachersSchoolsService.index(this.teacher.id, {
          page: page || forcePage || 1,
          itemsPerPage,
          filters: this.requestFilters,
        })
        .then((response) => {
            this.items = response.data.data;
            this.totalItems = response.data.meta.total;
            this.pagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.pagination, forcePage },
            );
        })
        .catch(error => {
          this.handleError(error, this.errors);
        })
        .finally(() => {
          this.loading = false;
        });
      },

      async deleteSchoolTeacher(id) {
          this.showConfirm({
            onConfirm: () => {
              this.loading = true;
              return teachersSchoolsService.remove(this.teacher.id, id)
              .then(() => {
                this.showToast({ type: 'success', message: this.$t('DELETED') });
                this.fetchSchoolTeachers(this.pagination.page);
              })
              .catch(error => {
                this.handleError(error, this.errors);
              })
              .finally(() => {
                this.loading = false;
              });
            },
          });
      },
    },
    async created() {
      await this.fetchSchoolTeachers();
    },
  };
</script>
